import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, of } from 'rxjs';
import { ApiV2Response, ListParameters, ListResponse } from '../models';

export abstract class RepositoryV2Service<T> {
  protected listCache: { [key: string]: ListResponse<T> } = {};

  constructor(protected http: HttpClient, protected endpoint: string) {}

  list(
    options: ListParameters = { per_page: 15, cache: false, page: 1, search: '' }
  ): Observable<ListResponse<T>> {
    const fromObject = Object.fromEntries(
      Object.entries(options).filter(([, value]) => value !== undefined && value !== null)
    ) as Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
    const params = new HttpParams({ fromObject });
    const cacheKey = `${this.endpoint}?${params.toString()}`;

    if (options.cache && this.listCache[cacheKey]) {
      return of(this.listCache[cacheKey]);
    }

    const URL = this.endpoint;
    return this.http.get<ApiV2Response<ListResponse<T>>>(URL, { params }).pipe(
      //  catchError(() =>
      //   of({ data: null, status: 'error' } as ApiV2Response<ListResponse<T>>)
      // ),
      map((response) => {
        const transformedResponse = {
          ...response.data,
          data: response.data?.data?.map((item) => this.transform(item)),
        };
        this.listCache[cacheKey] = transformedResponse;
        return transformedResponse;
      })
    );
  }

  clearListCache(): void {
    this.listCache = {};
  }

  get(id: number, options?: Record<string, string | number | boolean>): Observable<ApiV2Response<T>> {
    const URL = this.endpoint + '/' + id;
    const params = options ? new HttpParams({ fromObject: options }) : undefined;
    return this.http.get<ApiV2Response<T>>(URL, { params }).pipe(
      // catchError(() => of({ data: null, status: 'error' } as ApiV2Response<T>)),
      map((response) => ({ ...response, data: this.transform(response.data) }))
    );
  }

  create(data: unknown): Observable<ApiV2Response<T>> {
    const URL = this.endpoint;
    return this.http.post<ApiV2Response<T>>(URL, this.untransform(data as T)).pipe(
      map((response) => ({
        ...response,
        data: this.transform(response.data),
      }))
    );
  }

  update(data: unknown, id: number): Observable<ApiV2Response<T>> {
    const URL = this.endpoint + '/' + id;
    return this.http.put<ApiV2Response<T>>(URL, this.untransform(data as T)).pipe(
      map((response) => ({
        ...response,
        data: this.transform(response?.data),
      }))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateStatus(data: any, id: number) {
    const URL = this.endpoint + '/update-status/' + id;
    return this.http.put<ApiV2Response<T>>(URL, this.untransform(data)).pipe(
      map((response) => ({
        ...response,
        data: this.transform(response.data),
      }))
    );
  }

  patch(data: Partial<T>, id: number): Observable<ApiV2Response<T>> {
    const URL = this.endpoint + '/' + id;
    return this.http.patch<ApiV2Response<T>>(URL, this.partialUntransform(data)).pipe(
      map((response) => ({
        ...response,
        data: this.transform(response.data),
      }))
    );
  }

  delete(id: number) {
    const URL = this.endpoint + '/' + id;
    return this.http.delete<ApiV2Response<T>>(URL);
  }

  partialUntransform(data: Partial<T>): unknown {
    return data as unknown;
  }

  untransform(data: T): unknown {
    return data as unknown;
  }
  abstract transform(data: unknown): T;
}
